import { merge } from 'rxjs';
import { map, filter, distinctUntilChanged, ignoreElements, tap } from 'rxjs/operators';
import {
  ContainerController,
  VisualPromptContainerElementController,
  ResponsiveLayoutScrollDirection,
} from '@sas-dvr/ltjs-bird';
import { ofActionCreatorType } from '../util/rxUtils';
import { from } from 'rxjs';
import { AnyAction } from '@sas/nova-commons/action';
import { StyleElement, Style } from '@sas-dvr/ltjs-commons';
import { getBackgroundColorString } from '@sas-dvr/ltjs-commons/util/StyleUtils';
import { StyleState } from '@sas-dvr/internal-va-react-core/store/style/StyleState';
import { getTextStyles } from './promptElementImpl/utils';
import { PromptContainerState } from '@sas-dvr/internal-va-react-core/store/birdElement/promptContainerState';
import { VisualElementControllerLoader } from './LoaderFactory';
import { loadVisualContainerElementController } from './loadVisualContainerElement';
import { BirdLayoutState } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { getPromptLayoutConstraints } from './loadPromptElement';

export const loadVisualPromptContainerElementController: VisualElementControllerLoader = (
  promptController,
  elementController,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(promptController instanceof ContainerController)) {
    throw new Error('Expected the controller to be a ContainerController');
  }
  if (!(elementController instanceof VisualPromptContainerElementController)) {
    throw new Error(
      'Expected the element controller to be a VisualPromptContainerElementController'
    );
  }
  const key = deps.getKey(promptController);

  const layoutType = promptController.getResponsiveLayoutScrollDirection();
  const model = elementController.getModel();

  const actions = [] as AnyAction[];

  // Style override actions
  const styleChain = promptController.getContainerBackgroundStyleChain();
  if (styleChain) {
    actions.push(StyleState.actions.set(key, getTextStyles(styleChain)));
  }
  const style = elementController.getStyle();
  if (style) {
    const bg = getButtonBarColorOverride(style);
    actions.push(StyleState.actions.set(key + '-buttonbar', bg ? { normal: { bg } } : undefined));
  }

  // Prompt containers in prompt headers should set prompt constraints
  const promptLayout = getPromptLayoutConstraints(promptController);
  if (promptLayout) {
    actions.push(BirdLayoutState.actions.setPromptConstraints(key, promptLayout));
  }

  return merge(
    loadVisualContainerElementController(
      promptController,
      elementController,
      unload$,
      myActions$,
      state$,
      deps,
      {
        create: () =>
          PromptContainerState.actions.add(key, {
            label: model?.getButtonText() ?? '',
            isHorizontal: layoutType === ResponsiveLayoutScrollDirection.HORIZONTAL,
            autoApplyValues: !elementController.isActionEnabled(),
            isOpen: false,
            autoClose: true,
          }),
        delete: () => PromptContainerState.actions.delete(key),
      }
    ),
    from(actions),
    myActions$.pipe(
      ofActionCreatorType<typeof PromptContainerState.actions.notifyApplyChanges>(
        PromptContainerState.actionTypes.notifyApplyChanges
      ),
      tap(() => elementController.applyDeferredPromptValues()),
      ignoreElements()
    ),
    myActions$.pipe(
      ofActionCreatorType<typeof PromptContainerState.actions.notifyCancelChanges>(
        PromptContainerState.actionTypes.notifyCancelChanges
      ),
      tap(() => elementController.cancelDeferredPromptValues()),
      ignoreElements()
    ),
    state$.pipe(
      map((aState) => aState.birdElements.PromptContainer[key]),
      filter((myState): myState is Exclude<typeof myState, undefined> => myState !== undefined),
      distinctUntilChanged(),
      tap((myState) => elementController.setActive(myState.isOpen)),
      ignoreElements()
    )
  );
};

// We are currently using the .ButtonBar class for both the color of the buttons in button bar prompts
// and the background color of the bottom portion of the prompt container. Because of this inconsistency,
// we only want to color the prompt container button area if there is an override set (i.e. we do not want
// to use the default button bar color). This is a hack to match VAN's behavior and should not be reused.
function getButtonBarColorOverride(style: Style): string | null {
  const parentStyle = style.getParentStyle();
  if (parentStyle) {
    // attributeBundle.hasOverrideForStyleAttribute(StyleAttribute.BACKGROUND_COLOR) always returns true,
    // so we need to manually check whether the parent style has a bar attribute bundle.
    const attributeBundle = style.getAttributeBundleForPredefinedStyleElement(
      StyleElement.BUTTONBAR
    );
    const parentAttributeBundle = parentStyle.getAttributeBundleForPredefinedStyleElement(
      StyleElement.BUTTONBAR
    );
    if (attributeBundle && parentAttributeBundle && attributeBundle !== parentAttributeBundle) {
      return getBackgroundColorString(attributeBundle);
    }
  }
  return null;
}
